import React, { useState, useEffect, useRef, useMemo } from 'react';
import Confetti from '../ui/Confetti';
import StatBox from '../ui/StatBox';
import useInterval from '../../hooks/useInterval';
import { playVroomSound, playWrongSound, playGameOverSound, playSuccessSound, playEngineIdleSound } from '../../utils/audio';
import { api } from '../../utils/api';
import DifficultySelectionModal from '../modals/DifficultySelectionModal';

type Opponent = {
  id: number;
  position: number;
  raceWpm: number; // The speed for this specific race
  emoji: string;
};

type Difficulty = 'Easy' | 'Medium' | 'Hard';

const CarRaceGame: React.FC = () => {
    const [words, setWords] = useState<string[]>([]);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [typedText, setTypedText] = useState("");
    const [startTime, setStartTime] = useState<number | null>(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [currentWPM, setCurrentWPM] = useState(0);
    const [totalTime, setTotalTime] = useState<number | null>(null);
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'win' | 'gameOver'>('ready');
    const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
    const [opponents, setOpponents] = useState<Opponent[]>([]);
    const [opponentAudio, setOpponentAudio] = useState<{ oscillator: OscillatorNode; gainNode: GainNode } | null>(null);
    const [playerPosition, setPlayerPosition] = useState(0);
    const [playerRank, setPlayerRank] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const BASE_WPM = 10; // The player's car is always moving at this minimum speed.
    const totalChars = useMemo(() => words.length > 0 ? words.join(' ').length : 0, [words]);
    
    const getRankSuffix = (rank: number | null) => {
        if (rank === null) return '';
        if (rank % 100 >= 11 && rank % 100 <= 13) return 'th';
        switch (rank % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const calculateFinalRankings = (finalPlayerPos: number) => {
        const allRacers = [
            { id: 'player', position: finalPlayerPos },
            ...opponents.map(opp => ({ id: opp.id, position: opp.position }))
        ];

        allRacers.sort((a, b) => b.position - a.position);
        const playerIndex = allRacers.findIndex(racer => racer.id === 'player');
        setPlayerRank(playerIndex + 1);
    };

    useInterval(() => {
        if (gameState !== 'playing' || !startTime || totalChars === 0) return;

        const now = Date.now();
        const elapsedMinutes = (now - startTime) / 60000;
        setElapsedTime(now - startTime);

        // Update player's actual WPM for display
        const charsTypedSoFar = words.slice(0, currentWordIndex).reduce((acc, word) => acc + word.length + 1, 0) + typedText.length;
        const wpm = elapsedMinutes > 0 ? Math.round((charsTypedSoFar / 5) / elapsedMinutes) : 0;
        setCurrentWPM(wpm);

        // Update player's position (base speed + progress)
        const baseProgress = (BASE_WPM * 5 * elapsedMinutes) / totalChars;
        const wordProgress = currentWordIndex / words.length;
        const newPlayerPosition = Math.min(100, (baseProgress + wordProgress) * 100);
        setPlayerPosition(newPlayerPosition);

        if (newPlayerPosition >= 100) {
            setGameState('win');
            setTotalTime(now - startTime);
            calculateFinalRankings(100);
            playSuccessSound();
            return;
        }

        // Update opponents and check for loss
        let opponentFinished = false;
        const updatedOpponents = opponents.map(opp => {
            if (opp.position >= 100) return opp;
            const oppCharsTyped = opp.raceWpm * 5 * elapsedMinutes;
            const progress = Math.min(100, (oppCharsTyped / totalChars) * 100);
            if (progress >= 100) {
                opponentFinished = true;
            }
            return { ...opp, position: progress };
        });
        setOpponents(updatedOpponents);

        if (opponentFinished) {
            setGameState('gameOver');
            setTotalTime(now - startTime);
            calculateFinalRankings(newPlayerPosition);
            playGameOverSound();
        }
    }, 100);

    useEffect(() => {
        if (gameState === 'playing' && !opponentAudio) {
            const audioNodes = playEngineIdleSound();
            if (audioNodes) {
                audioNodes.oscillator.start();
                setOpponentAudio(audioNodes);
            }
        } else if (gameState !== 'playing' && opponentAudio) {
            opponentAudio.oscillator.stop();
            opponentAudio.gainNode.disconnect();
            opponentAudio.oscillator.disconnect();
            setOpponentAudio(null);
        }

        return () => {
            if (opponentAudio) {
                try {
                  opponentAudio.oscillator.stop();
                  opponentAudio.gainNode.disconnect();
                  opponentAudio.oscillator.disconnect();
                } catch (e) {
                    // Ignore error if already stopped
                }
            }
        };
    }, [gameState, opponentAudio]);

    const setupOpponents = (currentDifficulty: Difficulty) => {
        let baseOpponentsConfig;
        switch (currentDifficulty) {
            case 'Hard':
                baseOpponentsConfig = [ { wpm: 50, variance: 5 }, { wpm: 55, variance: 8 }, { wpm: 60, variance: 10 } ];
                break;
            case 'Medium':
                baseOpponentsConfig = [ { wpm: 35, variance: 5 }, { wpm: 40, variance: 8 }, { wpm: 45, variance: 10 } ];
                break;
            case 'Easy':
            default:
                baseOpponentsConfig = [ { wpm: 20, variance: 5 }, { wpm: 25, variance: 8 }, { wpm: 30, variance: 10 } ];
                break;
        }

        const emojis = ['🚓', '🚕', '🚚', '🚑', '🚒'];
        const shuffledEmojis = [...emojis].sort(() => 0.5 - Math.random());

        setOpponents(baseOpponentsConfig.map((o, i) => ({
            id: i + 1,
            position: 0,
            raceWpm: o.wpm + (Math.random() - 0.5) * o.variance,
            emoji: shuffledEmojis[i]
        })));
    };

    const startGame = async (selectedDifficulty: Difficulty) => {
        let wordCount;
        switch (selectedDifficulty) {
            case 'Hard': wordCount = 25; break;
            case 'Medium': wordCount = 20; break;
            case 'Easy': default: wordCount = 15; break;
        }

        const data = await api.get<string[]>(`/api/v1/game/words?count=${wordCount}`);
        setWords(data);
        setupOpponents(selectedDifficulty);

        setGameState('playing');
        setCurrentWordIndex(0);
        setTypedText("");
        setTotalTime(null);
        setElapsedTime(0);
        setCurrentWPM(0);
        setPlayerPosition(0);
        setPlayerRank(null);
        setStartTime(Date.now());
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSelectDifficulty = (selected: Difficulty) => {
        setDifficulty(selected);
        startGame(selected);
    };

    const resetGame = () => {
        setDifficulty(null);
        setGameState('ready');
        setPlayerPosition(0);
        setPlayerRank(null);
    };

    const currentWord = words[currentWordIndex];

    const handleCorrectWord = () => {
        playVroomSound();
        setCurrentWordIndex(i => i + 1);
        setTypedText("");
    };

    const handleIncorrectWord = () => {
        playWrongSound();
        const wordDisplay = document.getElementById('word-display-car-race');
        if (wordDisplay) {
            wordDisplay.classList.add('shake');
            setTimeout(() => wordDisplay.classList.remove('shake'), 820);
        }
    };

    const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(gameState !== 'playing') return;
        const value = e.target.value;
        if (value.endsWith(' ')) {
            if (value.trim() === currentWord) {
                handleCorrectWord();
            } else {
                handleIncorrectWord();
            }
        } else {
            setTypedText(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (gameState !== 'playing') return;
        if (e.key === 'Enter') {
            e.preventDefault();
            if (typedText === currentWord) {
                handleCorrectWord();
            } else {
                handleIncorrectWord();
            }
        }
    };

    if (gameState === 'ready') return <DifficultySelectionModal onSelectDifficulty={handleSelectDifficulty} onClose={() => {}} />;
    
    if ((gameState === 'win' || gameState === 'gameOver') && totalTime) {
        return (
            <div className="text-center">
                {gameState === 'win' && <Confetti />}
                <h3 className={`text-3xl font-bold ${gameState === 'win' ? 'text-yellow-400' : 'text-red-500'}`}>
                    {gameState === 'win' ? 'You Win! 🏆' : 'You Lost!'}
                </h3>
                {playerRank && <p className="text-2xl mt-4">You finished {playerRank}{getRankSuffix(playerRank)}!</p>}
                <p className="text-xl mt-2">
                    {gameState === 'gameOver' 
                        ? `An opponent finished before you on ${difficulty}. Keep practicing!`
                        : `Your time on ${difficulty} was ${(totalTime / 1000).toFixed(2)} seconds.`
                    }
                </p>
                <button onClick={resetGame} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold">
                    {gameState === 'win' ? 'Play Again' : 'Race Again'}
                </button>
            </div>
        );
    }

    return(
        <div className="w-full" onClick={() => inputRef.current?.focus()}>
             <div className="flex justify-around mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
                <StatBox label="Time" value={(elapsedTime / 1000).toFixed(1)} />
                <StatBox label="Speed" value={`${currentWPM} WPM`} />
                <StatBox label="Progress" value={`${currentWordIndex} / ${words.length}`} />
            </div>
            <div className="relative h-64 bg-gray-600 rounded-lg overflow-hidden mb-4 p-4 flex flex-col justify-end" style={{background: 'linear-gradient(to bottom, #87CEEB 0%, #333 60%)'}}>
                {/* Road markings */}
                {[...Array(3)].map((_, i) => (
                     <div key={i} className="absolute w-full h-1 bg-yellow-300" style={{top: `${22 + i * 23}%`, background: 'repeating-linear-gradient(90deg, #fff, #fff 10px, #333 10px, #333 20px)'}}></div>
                ))}
                
                {/* Cars */}
                <div className="absolute text-5xl transition-all duration-500 ease-out" style={{left: `calc(${playerPosition}% - 40px)`, bottom: '5%'}}>
                    <div className="transform scale-x-[-1]">🏎️</div>
                </div>

                {opponents.map((opp, index) => (
                    <div key={opp.id} className="absolute text-5xl transition-all duration-200" style={{left: `calc(${opp.position}% - 40px)`, bottom: `${28 + index * 23}%`}}>
                       <div className="transform scale-x-[-1]">{opp.emoji}</div>
                    </div>
                ))}
                
                {/* Finish Lines */}
                 {[...Array(4)].map((_, i) => (
                     <div key={i} className="absolute text-5xl" style={{right: '10px', bottom: `${5 + i * 23}%`}}>🏁</div>
                 ))}
            </div>

             <div id="word-display-car-race" className="p-4 md:p-6 mb-6 bg-gray-100 rounded-lg text-center font-mono text-3xl md:text-4xl" style={{ minHeight: '80px' }}>
               {currentWord?.split("").map((char, charIndex) => {
                    let charClass = "text-gray-500";
                    if (charIndex < typedText.length) charClass = typedText[charIndex] === char ? "text-green-600" : "text-red-500 underline";
                    return <span key={charIndex} className={charClass}>{char}</span>;
                })}
            </div>
            
            <input ref={inputRef} type="text" value={typedText} onChange={handleTyping} onKeyDown={handleKeyDown} className="opacity-0 absolute w-0 h-0" autoFocus />
        </div>
    );
};

export default CarRaceGame;